import React, { FC } from 'react';
import { 
  LayoutDashboard, 
  Utensils, 
  LifeBuoy, 
  ClipboardList, 
  CreditCard, 
  Sparkles, 
  History, 
  QrCode,
  Globe2,
  Lock,
  Menu,
  X,
  Users,
  Shield
} from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  openTicketsCount: number;
  allowedTabs?: string[];
}

export const Sidebar: FC<SidebarProps> = ({ activeTab, setActiveTab, openTicketsCount, allowedTabs }) => {
  const menuItems = [
    { id: 'dashboard', label: 'Metrik Paneli', icon: LayoutDashboard },
    { id: 'restaurants', label: 'Restoranlar', icon: Utensils },
    { 
      id: 'support', 
      label: 'Müştəri Dəstəyi', 
      icon: LifeBuoy,
      badge: openTicketsCount > 0 ? openTicketsCount : undefined 
    },
    { id: 'tasks', label: 'Rəqəmsallaşdırma', icon: ClipboardList },
    { id: 'workload', label: 'Komanda Yükü & Reytinqi', icon: Users },
    { id: 'plans', label: 'Restoran Ödənişləri', icon: CreditCard },
    { id: 'ai-copilot', label: 'AI Analitik', icon: Sparkles, highlight: true },
    { id: 'team', label: 'Komanda & Giriş', icon: Shield },
    { id: 'logs', label: 'Sistem Auditi', icon: History }
  ];

  // Map database permission aliases to UI Tab IDs:
  // Database saves 'summary', but UI has 'dashboard'.
  const normalizedAllowedTabs = allowedTabs 
    ? allowedTabs.map(t => t === 'summary' ? 'dashboard' : t)
    : undefined;

  const filteredItems = normalizedAllowedTabs
    ? menuItems.filter(item => normalizedAllowedTabs.includes(item.id))
    : menuItems;

  return (
    <aside className="w-80 bg-slate-900 text-slate-100 flex flex-col h-screen border-r border-slate-800 shadow-xl overflow-y-auto">
      {/* Platform Branding Header */}
      <div className="p-6 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600 rounded-lg text-white shadow-lg shadow-indigo-600/30">
            <QrCode className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-lg tracking-tight leading-none text-white">
              QR Menu <span className="text-indigo-400">HQ</span>
            </h1>
            <p className="text-[10px] text-slate-400 font-mono mt-1 tracking-wider uppercase">
              Super Admin Konsolu
            </p>
          </div>
        </div>
      </div>

      {/* Main Navigation Links */}
      <div className="flex-1 px-4 py-6 space-y-1.5">
        <div className="px-3 mb-2 text-[10px] font-mono font-semibold tracking-widest text-slate-500 uppercase">
          Baş İdarəetmə Paneli
        </div>
        
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg transition-all duration-200 text-sm font-medium ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : item.highlight
                  ? 'text-indigo-300 hover:bg-slate-800/60 hover:text-indigo-200 bg-indigo-950/20 border border-indigo-900/40'
                  : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : item.highlight ? 'text-indigo-400' : 'text-slate-400'}`} />
                <span>{item.label}</span>
              </div>
              
              {item.badge !== undefined && (
                <span className="flex items-center justify-center px-1.5 py-0.5 text-[10px] font-mono font-bold text-white bg-rose-500 rounded-full min-w-4 shadow-sm animate-bounce">
                  {item.badge}
                </span>
              )}

              {item.highlight && !isActive && (
                <span className="inline-flex items-center rounded-md bg-indigo-400/10 px-1.5 py-0.5 text-[9px] font-medium text-indigo-400 border border-indigo-400/20">
                  AI Beta
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Operator Session State (Anti-AI-slop professional metadata) */}
      <div className="p-5 border-t border-slate-800 bg-slate-950/40 shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-mono font-medium text-slate-300">
              AY
            </div>
            <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-slate-200 truncate leading-tight">
              aliyusifow@gmail.com
            </p>
            <p className="text-[10px] text-emerald-400 font-medium font-mono tracking-wide mt-0.5 uppercase">
              Baş Admin
            </p>
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-2 justify-between text-[11px] text-slate-500 border-t border-slate-800/60 pt-3">
          <div className="flex items-center gap-1">
            <Globe2 className="w-3 h-3 text-slate-600" />
            <span className="font-mono">Güvənli SSL</span>
          </div>
          <div className="flex items-center gap-1 font-mono">
            <span>Server CJS</span>
          </div>
        </div>
      </div>
    </aside>
  );
};
